import type { DistributiveOmit } from "@/types";
import { type ReactElement, useMemo } from "react";

import { DEFAULT_BODY_PROPS } from "@/constants";
import { useGetCurrentSizeClass, useGetMotionProfile } from "@/hooks";
import { useConvertSxToEmotionStyles } from "@/hooks/useConvertSxToEmotionStyles";
import { useTheme } from "@/hooks/useTheme";
import type { BodyProps } from "@/types";
import { cloneElementWithCssProp, getMotionProfileSx, mergeSx } from "@/utils";
import { getBodyTextStyles } from "@/utils/textStyleHelpers";

import { MultiTextShimmer } from "../MultiTextShimmer";
import { baseTextComponentSx } from "../shared";

function BodyContent<RC extends ReactElement | undefined = undefined>({
  size = DEFAULT_BODY_PROPS.size,
  rc = DEFAULT_BODY_PROPS.rc,
  weight = DEFAULT_BODY_PROPS.weight,
  sx,
  testId,
  mono,
  children,
  domRef,
  className,
  motionProfile,
  ...props
}: DistributiveOmit<BodyProps<RC>, "shimmer" | "shimmerSx" | "shimmerLines">) {
  const themeProps = useTheme();
  const motionProfileToUse = useGetMotionProfile(motionProfile);
  const mergedSx = useMemo(
    () =>
      mergeSx(
        baseTextComponentSx,
        { c: DEFAULT_BODY_PROPS.color },
        getBodyTextStyles({ themeProps, mono, size, weight }),
        getMotionProfileSx(motionProfileToUse, themeProps),
        themeProps.components?.Body?.sxOverride ?? {},
        sx,
      ),
    [sx, themeProps, mono, size, weight, motionProfileToUse],
  );
  const css = useConvertSxToEmotionStyles(mergedSx);
  const currentSizeClass = useGetCurrentSizeClass(
    size,
    DEFAULT_BODY_PROPS.size,
    Object.keys(themeProps.base.text.body),
  );

  return cloneElementWithCssProp(rc, {
    ...props,
    ...(testId ? { "data-testid": testId } : {}),
    ...(domRef ? { ref: domRef } : {}),
    css,
    children,
    className: `${className ?? ""} Body Body--${currentSizeClass}`,
  });
}

export function Body<RC extends ReactElement | undefined = undefined>({
  size = DEFAULT_BODY_PROPS.size,
  rc = DEFAULT_BODY_PROPS.rc,
  weight = DEFAULT_BODY_PROPS.weight,
  shimmer,
  shimmerSx,
  sx,
  children,
  mono,
  domRef,
  testId = "Body",
  className,
  motionProfile,
  ...props
}: BodyProps<RC>) {
  return shimmer ? (
    <MultiTextShimmer
      textKind="Body"
      size={size}
      sx={shimmerSx}
      shimmerLines={shimmer}
      rc={rc}
      className={className}
      testId={`${testId}__shimmer`}
    />
  ) : (
    <BodyContent
      {...props}
      size={size}
      sx={sx}
      weight={weight}
      mono={mono}
      rc={rc}
      domRef={domRef}
      testId={testId}
      className={className}
      motionProfile={motionProfile}
    >
      {children}
    </BodyContent>
  );
}

Body.displayName = "Body";
